// 운영 서랍 「회원 화면 보기」 (2026-10-20 시안 C · 기획 docs/ops/admin-view-as-drawer-plan.md ·
// 시안 docs/design/admin-view-as-home-variants.html)
//
// 홈(TopNavBar)·영상권리사(VideoRightsTopBar) 상단 운영 띠의 「다른 사용자 이메일」 입력을 대신한다.
//   - 이름·닉네임·이메일·회사로 찾고(관리자 「사용자」와 같은 ilike OR · 8건), 결과를 누르면 요약 카드
//     (등급·가입 승인·채널·정산 정보·추천 코드)가 뜨고 「이 회원 화면 보기」로 연다.
//   - 열기는 종전과 똑같이 `localStorage.impersonateEmail` + 새로고침 — UserContext 의 Admin 재확인,
//     ImpersonateNotice 경고 띠, 로그아웃 때 삭제 등 기존 판정 코드는 그대로 돈다.
//   - 최근 본 회원 5명은 이 브라우저에만(`impersonateRecent`). 검색어가 없을 때 먼저 보인다.
//   - 보는 중(viewingEmail)에는 맨 위에 그 회원의 요약 카드 + 「돌아가기」, 아래에 다른 회원 찾기.
// 🔒 전화번호는 조회하지도 보여 주지도 않는다. 운영자 역할이 아니면 상단바가 이 서랍을 렌더하지 않는다.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Spinner,
  Tag,
  Text,
  useToast,
} from "@chakra-ui/react";
import { Search, X, ExternalLink, Users, Eye } from "react-feather";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";
import { tt } from "../../i18n/tt";
import useLatestRequest from "../../hooks/useLatestRequest";
import { approvalCell, tierCell, payoutCell, ROW_UNAVAILABLE } from "../../lib/adminUserQueue";
import {
  USER_SELECT,
  SEARCH_LIMIT,
  readRecent,
  addRecent,
  removeRecent,
  searchFilter,
  looksLikeEmail,
  highlightParts,
  displayName,
  roleBadges,
} from "../../lib/impersonatePicker";

const TIER_SELECT = "user_email, band, status, is_dormant, rookie_progress_current, rookie_progress_target";
const DEBOUNCE_MS = 300;

/** 종전 handleImpersonate 와 같은 저장 + 새로고침. 최근 본 회원에도 남긴다. */
export function openAsUser(user, navigate) {
  if (!user?.email) return;
  addRecent({ email: user.email, name: displayName(user) });
  localStorage.setItem("impersonateEmail", user.email);
  navigate(0);
}

/** 「돌아가기」 — TopNavBar·VideoRightsTopBar 의 handleRevert 와 같다. */
export function revertImpersonation(navigate) {
  localStorage.removeItem("impersonateEmail");
  localStorage.removeItem("isActualAdmin");
  navigate(0);
}

function Highlight({ text, query }) {
  const parts = useMemo(() => highlightParts(text, query), [text, query]);
  return (
    <>
      {parts.map((p, i) =>
        p.hit ? (
          <Text as="mark" key={i} bg="transparent" color="brand.700" fontWeight="900">
            {p.text}
          </Text>
        ) : (
          <React.Fragment key={i}>{p.text}</React.Fragment>
        )
      )}
    </>
  );
}

function Initial({ user, size = 28 }) {
  const ch = displayName(user).slice(0, 1) || "?";
  return (
    <Flex
      w={`${size}px`}
      h={`${size}px`}
      borderRadius="full"
      bg="brand.50"
      color="brand.700"
      align="center"
      justify="center"
      fontSize={`${Math.round(size * 0.42)}px`}
      fontWeight="700"
      flexShrink={0}
      aria-hidden="true"
    >
      {ch}
    </Flex>
  );
}

const TONE = { red: "red", green: "green", neutral: "gray" };

function ApprovalTag({ user }) {
  const cell = approvalCell(user, null);
  return (
    <Tag size="sm" colorScheme={TONE[cell.tone] || "gray"} fontSize="11px" fontWeight="700" flexShrink={0}>
      {cell.key === "pending" ? cell.short : cell.label}
    </Tag>
  );
}

function ResultRow({ user, query, active, onPick, onOpen }) {
  const badges = roleBadges(user.role);
  return (
    <Flex
      as="button"
      type="button"
      w="100%"
      textAlign="left"
      align="center"
      gap={2.5}
      px={3}
      py={2}
      bg={active ? "brand.50" : "transparent"}
      _hover={{ bg: active ? "brand.50" : "neutral.50" }}
      borderTop="1px solid"
      borderColor="neutral.100"
      onClick={() => onPick(user)}
      onDoubleClick={() => onOpen(user)}
      data-testid="impersonate-result"
      aria-pressed={active}
    >
      <Initial user={user} />
      <Box flex="1" minW={0}>
        <Text fontSize="13.5px" fontWeight="700" lineHeight="1.3" noOfLines={1}>
          <Highlight text={displayName(user)} query={query} />
          {user.nick_name && user.nick_name !== user.name && (
            <Text as="span" fontWeight="400" color="neutral.500" ml={1.5}>
              <Highlight text={user.nick_name} query={query} />
            </Text>
          )}
        </Text>
        <Text fontSize="11px" color="neutral.500" fontFamily="mono" noOfLines={1}>
          <Highlight text={user.email} query={query} />
          {user.company && (
            <>
              {" · "}
              <Highlight text={user.company} query={query} />
            </>
          )}
        </Text>
      </Box>
      <HStack spacing={1} flexShrink={0} flexWrap="wrap" justify="flex-end">
        {badges.map((b) => (
          <Tag key={b} size="sm" colorScheme="purple" fontSize="11px" fontWeight="700">
            {b}
          </Tag>
        ))}
        <ApprovalTag user={user} />
      </HStack>
    </Flex>
  );
}

/** 요약 카드 — 관리자 「사용자」 표가 이미 읽는 값과 같은 조회(등급 최신 월 · 정산 정보 · 채널). 실패는 「확인 불가」. */
function useUserExtras(email, enabled) {
  const [extras, setExtras] = useState(null); // { channels, tier, payout } · null = 조회 중/없음
  const begin = useLatestRequest();
  useEffect(() => {
    if (!email || !enabled) {
      setExtras(null);
      return;
    }
    const isLatest = begin();
    setExtras(null);
    (async () => {
      const [chRes, monthRes, payRes] = await Promise.all([
        supabase.from("channel").select("channel_id, channel_title, type, status").eq("user", email).is("deleted_at", null),
        supabase.from("creator_tier_monthly").select("month").order("month", { ascending: false }).limit(1),
        supabase.from("payout_info").select("email, type").eq("email", email).maybeSingle(),
      ]);
      let tierRow = ROW_UNAVAILABLE;
      if (!monthRes.error) {
        const month = monthRes.data?.[0]?.month ?? null;
        if (!month) tierRow = null;
        else {
          const r = await supabase.from("creator_tier_monthly").select(TIER_SELECT).eq("month", month).eq("user_email", email).maybeSingle();
          tierRow = r.error ? ROW_UNAVAILABLE : r.data ?? null;
        }
      }
      if (!isLatest()) return;
      setExtras({
        channels: chRes.error ? null : chRes.data || [],
        tier: tierRow,
        payout: payRes.error ? ROW_UNAVAILABLE : payRes.data ?? null,
      });
    })();
  }, [email, enabled, begin]);
  return extras;
}

function SummaryCard({ user, extras, viewing, onOpen, onAdmin, onRevert }) {
  const tier = extras ? tierCell(extras.tier) : null;
  const payout = extras ? payoutCell(extras.payout) : null;
  const approval = approvalCell(user, null);
  const channels = extras?.channels;
  const badges = roleBadges(user.role);
  const Row = ({ label, children }) => (
    <>
      <Text as="dt" color="neutral.500" fontSize="12.5px" whiteSpace="nowrap">
        {label}
      </Text>
      <Text as="dd" m={0} fontSize="12.5px" color="neutral.800" minW={0}>
        {children}
      </Text>
    </>
  );
  const pending = <Text as="span" color="neutral.400">…</Text>;
  return (
    <Box border="1px solid" borderColor={viewing ? "purple.200" : "neutral.200"} borderRadius="10px" p={3} bg={viewing ? "purple.50" : "white"} data-testid="impersonate-summary">
      <Flex align="center" gap={2.5}>
        <Initial user={user} size={34} />
        <Box minW={0} flex="1">
          <Text fontSize="15px" fontWeight="700" lineHeight="1.3" noOfLines={1}>
            {displayName(user)}
            {user.nick_name && user.nick_name !== user.name && (
              <Text as="span" fontWeight="400" color="neutral.500" ml={1.5} fontSize="12.5px">
                {user.nick_name}
              </Text>
            )}
          </Text>
          <Text fontSize="11px" color="neutral.500" fontFamily="mono" noOfLines={1}>
            {user.email}
          </Text>
        </Box>
      </Flex>
      <Box as="dl" display="grid" gridTemplateColumns="auto 1fr" columnGap={3} rowGap={0.5} mt={2.5} mb={0}>
        <Row label={tt("a.0e1cd4b4", "등급")}>
          {tier ? (
            <>
              {tier.label}
              {tier.sub && (
                <Text as="span" color="neutral.500" ml={1}>
                  {tier.sub}
                </Text>
              )}
            </>
          ) : (
            pending
          )}
        </Row>
        <Row label={tt("a.c4ecf37f", "가입 승인")}>
          <Text as="span" color={approval.tone === "red" ? "red.600" : "green.700"} fontWeight="700">
            {approval.label}
          </Text>
          <Text as="span" color="neutral.500" ml={1}>
            {approval.sub}
          </Text>
        </Row>
        <Row label={tt("a.f0c83980", "채널")}>
          {!extras ? (
            pending
          ) : channels === null ? (
            tt("a.9496e597", "확인 불가")
          ) : (
            <>
              {tt("a.904fce3b", "{{v0}}개", { v0: channels.length })}
              {channels.length > 0 && (
                <Text as="span" color="neutral.500" ml={1} noOfLines={1}>
                  {channels
                    .slice(0, 3)
                    .map((c) => c.channel_title)
                    .filter(Boolean)
                    .join(" · ")}
                </Text>
              )}
            </>
          )}
        </Row>
        <Row label={tt("a.d7860153", "정산 정보")}>{payout ? payout.label : pending}</Row>
        <Row label={tt("a.8cb89c8c", "추천 코드")}>{user.signup_ref ? String(user.signup_ref).toUpperCase() : "—"}</Row>
        {badges.length > 0 && <Row label={tt("a.4cc01677", "역할")}>{badges.join(" · ")}</Row>}
      </Box>
      <Flex gap={1.5} mt={3} flexWrap="wrap">
        {viewing ? (
          <Button size="sm" variant="danger" onClick={onRevert} flex="1 1 auto">
            {tt("a.9576e83e", "돌아가기")}
          </Button>
        ) : (
          <Button size="sm" variant="primary" leftIcon={<Eye size={14} />} onClick={() => onOpen(user)} flex="1 1 auto" data-testid="impersonate-open">
            {tt("a.07e48966", "이 회원 화면 보기")}
          </Button>
        )}
        <Button size="sm" variant="outline" rightIcon={<ExternalLink size={13} />} onClick={() => onAdmin(user)} flexShrink={0}>
          {tt("a.0ac37c58", "관리자에서 열기")}
        </Button>
      </Flex>
    </Box>
  );
}

/**
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {string|null} [props.viewingEmail] 보는 중인 회원 이메일(`impersonateEmail`) — 있으면 맨 위에 그 회원 카드
 */
export default function ImpersonateDrawer({ isOpen, onClose, viewingEmail = null }) {
  const navigate = useNavigate();
  const toast = useToast();
  const inputRef = useRef(null);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null); // null = 검색 전, [] = 없음
  const [searching, setSearching] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [selected, setSelected] = useState(null); // 결과에서 고른 회원
  const [recent, setRecent] = useState([]);
  const [viewingUser, setViewingUser] = useState(null);
  const [directLoading, setDirectLoading] = useState(false);
  const beginSearch = useLatestRequest();

  // 열 때 최근 목록을 읽고, 보는 중이면 그 회원 행을 한 번 읽는다
  useEffect(() => {
    if (!isOpen) return;
    setRecent(readRecent());
    setQuery("");
    setResults(null);
    setSelected(null);
    setActiveIdx(0);
    if (!viewingEmail) {
      setViewingUser(null);
      return;
    }
    let alive = true;
    supabase
      .from("user")
      .select(USER_SELECT)
      .eq("email", viewingEmail)
      .maybeSingle()
      .then(({ data }) => {
        if (alive) setViewingUser(data || { email: viewingEmail });
      });
    return () => {
      alive = false;
    };
  }, [isOpen, viewingEmail]);

  // 검색 — 300ms 디바운스 · 2글자부터 · 마지막 요청만 반영
  useEffect(() => {
    if (!isOpen) return;
    const filter = searchFilter(query);
    if (!filter) {
      setResults(null);
      setSearching(false);
      return;
    }
    const isLatest = beginSearch();
    setSearching(true);
    const timer = setTimeout(async () => {
      const { data, error } = await supabase
        .from("user")
        .select(USER_SELECT)
        .or(filter)
        .order("created_date", { ascending: false })
        .limit(SEARCH_LIMIT);
      if (!isLatest()) return;
      setSearching(false);
      if (error) {
        console.error("회원 검색 실패:", error.message);
        toast({ title: tt("a.7ec75a95", "회원을 찾는 중 오류가 났어요."), description: error.message, status: "error", duration: 3500, isClosable: true });
        setResults([]);
        return;
      }
      const rows = (data || []).filter((u) => !u.deleted_at);
      setResults(rows);
      setActiveIdx(0);
      setSelected((prev) => (prev && rows.some((u) => u.email === prev.email) ? prev : rows.length === 1 ? rows[0] : null));
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, isOpen, beginSearch, toast]);

  const extras = useUserExtras(selected?.email, isOpen && !!selected);
  const viewingExtras = useUserExtras(viewingUser?.email, isOpen && !!viewingUser);

  const handleOpen = useCallback((user) => openAsUser(user, navigate), [navigate]);
  const handleRevert = useCallback(() => revertImpersonation(navigate), [navigate]);
  const handleAdmin = useCallback(
    (user) => {
      const to = `/admin/users?search=${encodeURIComponent(user.email)}`;
      if (viewingEmail) {
        // 보는 중에는 userInfo 가 그 회원이라 어드민 라우트가 막힌다 — 먼저 되돌리고 전체 이동
        localStorage.removeItem("impersonateEmail");
        localStorage.removeItem("isActualAdmin");
        window.location.assign(to);
        return;
      }
      onClose();
      navigate(to);
    },
    [navigate, onClose, viewingEmail]
  );

  /** 결과가 없는데 이메일 모양이면 — 종전처럼 가입 계정인지 확인한 뒤 바로 연다 */
  const handleDirect = useCallback(async () => {
    const email = query.trim();
    setDirectLoading(true);
    try {
      const { data, error } = await supabase.from("user").select(USER_SELECT).eq("email", email).maybeSingle();
      if (error) {
        toast({ title: tt("a.7ec75a95", "회원을 찾는 중 오류가 났어요."), description: error.message, status: "error", duration: 3500, isClosable: true });
        return;
      }
      if (!data) {
        toast({
          title: tt("a.e8f89e7c", "등록된 사용자가 아닙니다."),
          description: tt("a.8e20ae49", "{{v0}} 은(는) 레이블리 가입 계정이 아닙니다. (카카오/네이버 채널 이메일은 로그인 계정과 다를 수 있습니다.)", { v0: email }),
          status: "warning",
          duration: 5000,
          isClosable: true,
        });
        return;
      }
      handleOpen(data);
    } finally {
      setDirectLoading(false);
    }
  }, [query, toast, handleOpen]);

  const onKeyDown = (e) => {
    if (!results || results.length === 0) {
      if (e.key === "Enter" && looksLikeEmail(query) && results && results.length === 0) handleDirect();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = Math.min(activeIdx + 1, results.length - 1);
      setActiveIdx(next);
      setSelected(results[next]);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const next = Math.max(activeIdx - 1, 0);
      setActiveIdx(next);
      setSelected(results[next]);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const target = selected || results[activeIdx];
      if (target) handleOpen(target);
    }
  };

  const pick = (user) => {
    setSelected(user);
    const idx = (results || []).findIndex((u) => u.email === user.email);
    if (idx >= 0) setActiveIdx(idx);
  };

  const showRecent = !searchFilter(query);

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="sm" initialFocusRef={inputRef}>
      <DrawerOverlay bg="blackAlpha.500" />
      {/* 휴대폰은 전폭, PC 는 380px — useBreakpointValue 대신 CSS 반응형(jsdom 에 matchMedia 가 없다) */}
      <DrawerContent maxW={{ base: "100%", md: "380px" }} data-testid="impersonate-drawer">
        <DrawerCloseButton aria-label={tt("a.218e2ae6", "닫기")} />
        <DrawerHeader fontSize="15px" fontWeight="700" px={4} pt={3} pb={2} display="flex" alignItems="center" gap={2}>
          <Users size={16} />
          {viewingEmail ? tt("a.b2ad07a5", "운영팀이 보는 중") : tt("a.f2e3ba02", "회원 화면 보기")}
        </DrawerHeader>
        <DrawerBody px={4} pt={1} pb="calc(16px + env(safe-area-inset-bottom))" display="flex" flexDirection="column" gap={3}>
          {viewingEmail && (
            viewingUser ? (
              <SummaryCard user={viewingUser} extras={viewingExtras} viewing onOpen={handleOpen} onAdmin={handleAdmin} onRevert={handleRevert} />
            ) : (
              <Flex align="center" gap={2} color="neutral.500" fontSize="13px">
                <Spinner size="xs" /> {viewingEmail}
              </Flex>
            )
          )}

          {viewingEmail && (
            <Text fontSize="12px" fontWeight="700" color="neutral.500" mt={1}>
              {tt("a.aa26d3ce", "다른 회원")}
            </Text>
          )}

          <InputGroup size="md">
            <InputLeftElement pointerEvents="none" color="neutral.400">
              {searching || directLoading ? <Spinner size="xs" /> : <Search size={15} />}
            </InputLeftElement>
            <Input
              ref={inputRef}
              id="impersonate-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={tt("a.e1d6908d", "이름 · 닉네임 · 이메일 · 회사")}
              autoComplete="off"
              bg="white"
              borderColor="neutral.200"
              fontSize="14px"
              data-testid="impersonate-search"
            />
          </InputGroup>

          {/* 결과 */}
          {results && (
            <Box border="1px solid" borderColor="neutral.200" borderRadius="10px" overflow="hidden" bg="white">
              <Text fontSize="10.5px" letterSpacing=".08em" color="neutral.500" px={3} pt={2} pb={1} fontFamily="mono" textTransform="uppercase">
                {tt("a.c73309a6", "검색 결과")} {results.length}
              </Text>
              {results.length === 0 ? (
                <Box px={3} pb={3} fontSize="13px" color="neutral.600">
                  <Text>{tt("a.d3b1ac83", "맞는 회원이 없어요.")}</Text>
                  {looksLikeEmail(query) && (
                    <Button size="sm" variant="outline" mt={2} onClick={handleDirect} isLoading={directLoading}>
                      {tt("a.790ddade", "이 이메일로 바로 열기")}
                    </Button>
                  )}
                </Box>
              ) : (
                results.map((u, i) => (
                  <ResultRow key={u.email} user={u} query={query} active={selected ? selected.email === u.email : i === activeIdx} onPick={pick} onOpen={handleOpen} />
                ))
              )}
            </Box>
          )}
          {!results && query.trim().length > 0 && (
            <Text fontSize="12px" color="neutral.500">
              {tt("a.316d24f9", "2글자 이상 입력하면 찾아요.")}
            </Text>
          )}

          {/* 고른 회원 요약 */}
          {selected && !showRecent && (
            <SummaryCard user={selected} extras={extras} viewing={false} onOpen={handleOpen} onAdmin={handleAdmin} onRevert={handleRevert} />
          )}

          {/* 최근 본 회원 */}
          {showRecent && (
            <Box border="1px solid" borderColor="neutral.200" borderRadius="10px" overflow="hidden" bg="white">
              <Text fontSize="10.5px" letterSpacing=".08em" color="neutral.500" px={3} pt={2} pb={1} fontFamily="mono" textTransform="uppercase">
                {tt("a.4cbd7a46", "최근 본 회원")}
              </Text>
              {recent.length === 0 ? (
                <Text px={3} pb={3} fontSize="13px" color="neutral.500">
                  {tt("a.5b82f8e3", "검색어를 지우면 최근 본 회원이 보여요.")}
                </Text>
              ) : (
                recent.map((r) => (
                  <Flex key={r.email} align="center" gap={2.5} px={3} py={2} borderTop="1px solid" borderColor="neutral.100" _hover={{ bg: "neutral.50" }}>
                    <Box as="button" type="button" display="flex" alignItems="center" gap={2.5} flex="1" minW={0} textAlign="left" onClick={() => handleOpen({ email: r.email, name: r.name })} data-testid="impersonate-recent">
                      <Initial user={{ name: r.name, email: r.email }} />
                      <Box minW={0}>
                        <Text fontSize="13.5px" fontWeight="700" lineHeight="1.3" noOfLines={1}>
                          {r.name || r.email}
                        </Text>
                        <Text fontSize="11px" color="neutral.500" fontFamily="mono" noOfLines={1}>
                          {r.email}
                        </Text>
                      </Box>
                    </Box>
                    <IconButton
                      aria-label={tt("a.955a18d0", "최근 목록에서 빼기")}
                      icon={<X size={14} />}
                      size="xs"
                      variant="ghost"
                      onClick={() => setRecent(removeRecent(r.email))}
                    />
                  </Flex>
                ))
              )}
            </Box>
          )}
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

/** 운영 띠의 진입 버튼 — 두 상단바가 같이 쓴다. */
export function ImpersonateDrawerButton({ onClick, ...rest }) {
  return (
    <Button size="sm" variant="primary" leftIcon={<Users size={14} />} onClick={onClick} flexShrink={0} data-guide="admin.view-as" {...rest}>
      {tt("a.f2e3ba02", "회원 화면 보기")}
    </Button>
  );
}

/** 보는 중 띠 — 이름·이메일 + 「다른 회원」(서랍) + 「돌아가기」. 종전 이메일만 있던 띠를 대신한다. */
export function ImpersonateViewingBar({ email, name, onOpenDrawer, onRevert }) {
  return (
    <Flex
      px={{ base: 4, md: 6 }}
      py={2}
      borderTop="1px solid"
      borderColor="purple.100"
      bg="purple.50"
      align="center"
      gap={2}
      flexWrap="wrap"
      data-testid="impersonate-viewing-bar"
    >
      <Flex align="center" gap={1.5} color="purple.800" minW={0} flex="1 1 auto">
        <Eye size={14} aria-hidden="true" />
        <Text fontSize="sm" fontWeight="700" noOfLines={1}>
          {tt("a.b2ad07a5", "운영팀이 보는 중")}
          {name ? ` · ${name}` : ""}
        </Text>
        <Text fontSize="11px" fontFamily="mono" color="purple.700" noOfLines={1} display={{ base: "none", sm: "block" }}>
          {email}
        </Text>
      </Flex>
      <Button size="sm" variant="outline" onClick={onOpenDrawer} flexShrink={0}>
        {tt("a.aa26d3ce", "다른 회원")}
      </Button>
      <Button size="sm" variant="danger" onClick={onRevert} flexShrink={0}>
        {tt("a.9576e83e", "돌아가기")}
      </Button>
    </Flex>
  );
}
